import React, { useEffect, useState } from 'react';
import { Calendar, Clock, CheckCircle2, Users2 } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';

interface Summary {
  id: string;
  title: string;
  date: string;
  time: string;
  summary: string | null;
  double: {
    name: string;
  };
}

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
}

const Dashboard = () => {
  const { user } = useAuth();
  const [todayActivities, setTodayActivities] = useState<Summary[]>([]);
  const [upcomingActivities, setUpcomingActivities] = useState<Summary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

        // Fetch today's summaries
        const { data: todayData, error: todayError } = await supabase
          .from('summaries')
          .select(`
            id,
            title,
            date,
            time,
            summary,
            double:double_id (
              name
            )
          `)
          .eq('date', today)
          .order('time');

        if (todayError) throw todayError;

        // Fetch upcoming summaries
        const { data: upcomingData, error: upcomingError } = await supabase
          .from('summaries')
          .select(`
            id,
            title,
            date,
            time,
            summary,
            double:double_id (
              name
            )
          `)
          .gte('date', tomorrow)
          .order('date')
          .order('time')
          .limit(5);

        if (upcomingError) throw upcomingError;

        setTodayActivities(todayData || []);
        setUpcomingActivities(upcomingData || []);
      } catch (err) {
        console.error('Error fetching activities:', err);
        setError('Failed to load activities');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [user?.id]);

  return (
    <div className="animate-fade-in">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
        </h1>
        <p className="text-gray-600">Here's what your doubles have been up to today.</p>
      </div>

      {error && (
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Activity Feed */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Today's Activities</h2>
          
          {loading ? (
            <div className="text-center py-8 text-gray-600">Loading activities...</div>
          ) : todayActivities.length > 0 ? (
            todayActivities.map((activity) => (
              <div 
                key={activity.id}
                className="bg-white p-6 rounded-lg border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">{activity.title}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <Clock className="h-4 w-4" />
                      <span>{activity.time}</span>
                      <span className="mx-2">•</span>
                      <span>with {activity.double.name}</span>
                    </div>
                  </div>
                </div>
                {activity.summary && (
                  <p className="text-gray-600">{activity.summary}</p>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-600">No activities scheduled for today</div>
          )}
        </div>

        {/* Upcoming Activities */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Upcoming Activities</h2>
          <div className="bg-white p-6 rounded-lg border-2 border-dark shadow-retro">
            {loading ? (
              <div className="text-center py-8 text-gray-600">Loading upcoming activities...</div>
            ) : upcomingActivities.length > 0 ? (
              <div className="space-y-6">
                {upcomingActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{activity.title}</div>
                      <div className="text-sm text-gray-600">
                        {new Date(activity.date).toLocaleDateString()} at {activity.time}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        with {activity.double.name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">No upcoming activities</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;